import type { ApiClient } from '@elite-residence/api-client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, type RouteObject, RouterProvider } from 'react-router';
import { AccountPage } from '../account/AccountPage';
import { ApiClientProvider } from '../api/ApiClientProvider';
import { AuditPage } from '../audit/AuditPage';
import {
  AcceptInvitationPage,
  ForgotPasswordPage,
  LoginPage,
  MfaPage,
  MfaSetupPage,
  ResetPasswordPage,
} from '../auth/AuthPages';
import { BoardPage } from '../booking/BoardPage';
import { BookingDetailPage, BookingsPage, MaintenancePage } from '../booking/BookingPages';
import { OperationsPage } from '../booking/OperationsPage';
import { SigningPage } from '../contract/ContractPages';
import { ContractsOverviewPage } from '../contract/ContractsOverviewPage';
import { CustomerDetailPage, CustomersPage } from '../customer/CustomerPages';
import { ExpensesPage, FinanceTaskPage, FinanceTasksPage } from '../finance/FinancePages';
import { FinanceReferencePage } from '../finance/FinanceReferencePage';
import { HomePage } from '../home/HomePage';
import { LocaleProvider, useLocale } from '../i18n/LocaleProvider';
import type { Locale } from '../i18n/messages';
import { SystemStatusPage } from '../pages/SystemStatusPage';
import { BranchDetailPage, BranchesPage, UnitPage } from '../property/PropertyPages';
import { ReferencePage } from '../reference/ReferencePage';
import { ReportsPage } from '../reports/ReportsPage';
import { StaffDetailPage, TeamPage } from '../team/TeamPages';
import { PublicFrame, SignedIn } from './Shell';

const Shell = PublicFrame;
const publicPage = (element: ReactNode): RouteObject['element'] => <PublicFrame>{element}</PublicFrame>;

function NotFoundPage() {
  const { messages } = useLocale();
  return (
    <main>
      <p role="alert">{messages.notFound}</p>
      <Link to="/">{messages.backHome}</Link>
    </main>
  );
}

function RouteError() {
  const { messages } = useLocale();
  return <p role="alert">{messages.unexpectedError}</p>;
}

// Feature modules add their routes here (see docs/engineering/ADDING_A_MODULE.md).
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <SignedIn>{(session) => <HomePage session={session} />}</SignedIn>,
    errorElement: <RouteError />,
  },
  { path: '/account', element: <SignedIn>{(session) => <AccountPage session={session} />}</SignedIn> },
  { path: '/audit', element: <SignedIn>{(session) => <AuditPage session={session} />}</SignedIn> },
  { path: '/team', element: <SignedIn>{(session) => <TeamPage session={session} />}</SignedIn> },
  { path: '/team/:staffId', element: <SignedIn>{(session) => <StaffDetailPage session={session} />}</SignedIn> },
  { path: '/property', element: <SignedIn>{(session) => <BranchesPage session={session} />}</SignedIn> },
  { path: '/property/units/:unitId', element: <SignedIn>{(session) => <UnitPage session={session} />}</SignedIn> },
  { path: '/property/:branchId', element: <SignedIn>{(session) => <BranchDetailPage session={session} />}</SignedIn> },
  { path: '/customers', element: <SignedIn>{(session) => <CustomersPage session={session} />}</SignedIn> },
  {
    path: '/customers/:customerId',
    element: <SignedIn>{(session) => <CustomerDetailPage session={session} />}</SignedIn>,
  },
  { path: '/bookings', element: <SignedIn>{(session) => <BookingsPage session={session} />}</SignedIn> },
  { path: '/board', element: <SignedIn>{(session) => <BoardPage session={session} />}</SignedIn> },
  { path: '/operations', element: <SignedIn>{(session) => <OperationsPage session={session} />}</SignedIn> },
  { path: '/contracts', element: <SignedIn>{(session) => <ContractsOverviewPage session={session} />}</SignedIn> },
  { path: '/reports', element: <SignedIn>{(session) => <ReportsPage session={session} />}</SignedIn> },
  {
    path: '/bookings/:bookingId',
    element: <SignedIn>{(session) => <BookingDetailPage session={session} />}</SignedIn>,
  },
  { path: '/maintenance', element: <SignedIn>{(session) => <MaintenancePage session={session} />}</SignedIn> },
  { path: '/expenses', element: <SignedIn>{(session) => <ExpensesPage session={session} />}</SignedIn> },
  { path: '/finance', element: <SignedIn>{(session) => <FinanceTasksPage session={session} />}</SignedIn> },
  {
    path: '/finance/tasks/:transactionId',
    element: <SignedIn>{(session) => <FinanceTaskPage session={session} />}</SignedIn>,
  },
  { path: '/reference-data', element: <SignedIn>{(session) => <ReferencePage session={session} />}</SignedIn> },
  {
    path: '/finance-reference-data',
    element: <SignedIn>{(session) => <FinanceReferencePage session={session} />}</SignedIn>,
  },
  { path: '/sign', element: publicPage(<SigningPage />) },
  { path: '/login', element: publicPage(<LoginPage />) },
  { path: '/login/mfa', element: publicPage(<MfaPage />) },
  { path: '/login/mfa-setup', element: publicPage(<MfaSetupPage />) },
  { path: '/accept-invitation', element: publicPage(<AcceptInvitationPage />) },
  { path: '/forgot-password', element: publicPage(<ForgotPasswordPage />) },
  { path: '/reset-password', element: publicPage(<ResetPasswordPage />) },
  {
    path: '/system',
    element: (
      <Shell>
        <SystemStatusPage />
      </Shell>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '*',
    element: (
      <Shell>
        <NotFoundPage />
      </Shell>
    ),
  },
];

// No silent retries: failures are shown, not hidden behind repeated requests.
export const createQueryClient = (): QueryClient =>
  new QueryClient({ defaultOptions: { queries: { retry: false }, mutations: { retry: false } } });

type Router = Parameters<typeof RouterProvider>[0]['router'];

export function App(props: {
  readonly client: ApiClient;
  readonly router: Router;
  readonly queryClient: QueryClient;
  readonly locale: Locale;
}) {
  return (
    <QueryClientProvider client={props.queryClient}>
      <ApiClientProvider client={props.client}>
        <LocaleProvider initial={props.locale}>
          <RouterProvider router={props.router} />
        </LocaleProvider>
      </ApiClientProvider>
    </QueryClientProvider>
  );
}
